import { useEffect, useState } from 'react';

const RECAPTCHA_KEY = process.env.REACT_APP_CAPTCHA_KEY;
let recaptchaScriptLoaded = false;

/**
 * Lazy-loads Google reCAPTCHA script on first use.
 * Returns { recaptchaReady: boolean, recaptchaFailed: boolean }.
 *
 * CIPA compliance note:
 *   reCAPTCHA is exempt from consent gating under the security/functionality
 *   carveout.  It is a spam‑prevention control — not a tracking technology —
 *   and is load‑bearing for contact/careers forms.  Blocking it would deny
 *   the service (CIPA § 1798.100(d) "functionality necessary for the service").
 *   The script loads unconditionally when any form component mounts, matching
 *   the same rationale as security_storage: 'granted' in GCM defaults.
 */
export const useRecaptcha = () => {
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);

  useEffect(() => {
    if (recaptchaScriptLoaded) {
      if (window.grecaptcha) {
        setRecaptchaReady(true);
      }
      return;
    }

    if (!RECAPTCHA_KEY) {
      // No key configured (local dev without .env)
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      recaptchaScriptLoaded = true;
      setRecaptchaReady(true);
    };
    script.onerror = () => {
      recaptchaScriptLoaded = true; // don't retry repeatedly
      setRecaptchaFailed(true);
    };
    document.head.appendChild(script);
  }, []);

  return { recaptchaReady, recaptchaFailed };
};